import React, { useState, useEffect } from 'react';
import {
  Eye,
  Save,
  Loader2,
  Shield,
  Users,
  Lock,
  UserCheck,
  ToggleLeft,
  ToggleRight,
  AlertTriangle,
  Trash2
} from 'lucide-react';
import { getDemoConfig, updateDemoConfig } from '../api/demo';
import { toast } from 'react-toastify';
import LoadingSpinner from '../components/common/LoadingSpinner';

/**
 * Demo Settings Page
 * Super admin can configure demo mode settings
 */
const DemoSettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [config, setConfig] = useState({
    enabled: false,
    demo_username: 'demo',
    demo_password: '',
    allowed_names: '',
    restrict_to_allowed_names: false,
    blur_sensitive_figures: true
  });

  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await getDemoConfig();
      setConfig({
        ...response.data,
        demo_password: '' // Don't show password
      });
    } catch (error) {
      toast.error('Failed to load demo configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const updateData = {
        enabled: config.enabled,
        demo_username: config.demo_username,
        allowed_names: config.allowed_names,
        restrict_to_allowed_names: config.restrict_to_allowed_names,
        blur_sensitive_figures: config.blur_sensitive_figures
      };

      // Only include password if set
      if (newPassword) {
        updateData.demo_password = newPassword;
      }

      await updateDemoConfig(updateData);
      toast.success('Demo settings saved successfully');
      setNewPassword('');
    } catch (error) {
      toast.error('Failed to save demo settings');
    } finally {
      setSaving(false);
    }
  };

  // Parse allowed names for display
  const allowedNamesList = config.allowed_names
    ? config.allowed_names
        .split(',')
        .map((n) => n.trim())
        .filter((n) => n)
    : [];

  const addAllowedName = (name) => {
    if (!name.trim()) return;
    const currentNames = config.allowed_names
      ? config.allowed_names
          .split(',')
          .map((n) => n.trim())
          .filter((n) => n)
      : [];
    if (!currentNames.includes(name.trim())) {
      currentNames.push(name.trim());
      handleInputChange('allowed_names', currentNames.join(', '));
    }
  };

  const removeAllowedName = (nameToRemove) => {
    const currentNames = config.allowed_names
      .split(',')
      .map((n) => n.trim())
      .filter((n) => n && n !== nameToRemove);
    handleInputChange('allowed_names', currentNames.join(', '));
  };

  if (loading) {
    return <LoadingSpinner className="min-h-screen" />;
  }

  return (
    <div className="min-h-screen bg-secondary-offWhite p-4 sm:p-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center gap-3">
            <Eye className="w-8 h-8 text-accent-gold" />
            <div>
              <h1 className="text-2xl font-bold text-primary-black">Demo Settings</h1>
              <p className="text-secondary-gray text-sm">
                Configure demo mode for testing and presentations
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Enable/Disable Demo Mode */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {config.enabled ? (
                  <ToggleRight className="w-6 h-6 text-green-500" />
                ) : (
                  <ToggleLeft className="w-6 h-6 text-secondary-gray" />
                )}
                <div>
                  <h2 className="text-lg font-semibold text-primary-black">Demo Mode</h2>
                  <p className="text-sm text-secondary-gray">
                    {config.enabled ? 'Demo mode is active' : 'Demo mode is disabled'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleInputChange('enabled', !config.enabled)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  config.enabled
                    ? 'bg-red-100 text-red-600 hover:bg-red-200'
                    : 'bg-green-100 text-green-600 hover:bg-green-200'
                }`}
              >
                {config.enabled ? 'Disable' : 'Enable'}
              </button>
            </div>

            {config.enabled && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    Demo mode is active. Anyone with the demo credentials can access the app in
                    view-only mode.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Demo Credentials */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-primary-black mb-4 flex items-center gap-2">
              <Lock className="w-5 h-5 text-accent-gold" />
              Demo Credentials
            </h2>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-primary-black mb-2">
                  Username
                </label>
                <input
                  type="text"
                  value={config.demo_username}
                  onChange={(e) => handleInputChange('demo_username', e.target.value)}
                  className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
                  placeholder="demo"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-black mb-2">
                  Password{' '}
                  {config.id && (
                    <span className="text-secondary-gray">(leave blank to keep current)</span>
                  )}
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
                  placeholder={config.id ? '••••••••' : 'Set password'}
                />
              </div>
            </div>

            <p className="text-xs text-secondary-gray mt-2">
              Share these credentials with people who need demo access.
            </p>
          </div>

          {/* Allowed Names */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-primary-black flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-accent-gold" />
                Allowed Names
              </h2>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.restrict_to_allowed_names}
                  onChange={(e) => handleInputChange('restrict_to_allowed_names', e.target.checked)}
                  className="w-4 h-4 text-accent-gold border-secondary-lightGray rounded focus:ring-accent-gold"
                />
                <span className="text-sm text-secondary-gray">Restrict to list</span>
              </label>
            </div>

            {config.restrict_to_allowed_names ? (
              <>
                <p className="text-sm text-secondary-gray mb-3">
                  Only these names can access demo mode:
                </p>

                {/* Names list */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {allowedNamesList.length > 0 ? (
                    allowedNamesList.map((name, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-accent-gold/10 text-primary-black rounded-full text-sm"
                      >
                        {name}
                        <button
                          type="button"
                          onClick={() => removeAllowedName(name)}
                          className="hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </span>
                    ))
                  ) : (
                    <span className="text-secondary-gray text-sm italic">No names added yet</span>
                  )}
                </div>

                {/* Add name input */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Add a name..."
                    className="flex-1 px-4 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addAllowedName(e.target.value);
                        e.target.value = '';
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      const input = e.target.previousSibling;
                      addAllowedName(input.value);
                      input.value = '';
                    }}
                    className="px-4 py-2 bg-primary-charcoal text-white rounded-lg hover:bg-primary-black transition-colors"
                  >
                    Add
                  </button>
                </div>
              </>
            ) : (
              <div className="p-4 bg-secondary-offWhite rounded-lg text-center">
                <Users className="w-8 h-8 text-secondary-gray mx-auto mb-2" />
                <p className="text-secondary-gray text-sm">
                  Anyone with the demo credentials can access demo mode.
                </p>
                <p className="text-xs text-secondary-gray mt-1">
                  Enable "Restrict to list" to limit access to specific names.
                </p>
              </div>
            )}
          </div>

          {/* Blur Sensitive Figures */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Shield className="w-6 h-6 text-accent-gold" />
                <div>
                  <h2 className="text-lg font-semibold text-primary-black">
                    Blur Sensitive Figures
                  </h2>
                  <p className="text-sm text-secondary-gray">
                    {config.blur_sensitive_figures
                      ? 'Financial figures are hidden from demo users'
                      : 'Financial figures are visible to demo users'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() =>
                  handleInputChange('blur_sensitive_figures', !config.blur_sensitive_figures)
                }
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  config.blur_sensitive_figures
                    ? 'bg-green-100 text-green-600 hover:bg-green-200'
                    : 'bg-amber-100 text-amber-600 hover:bg-amber-200'
                }`}
              >
                {config.blur_sensitive_figures ? 'Enabled' : 'Disabled'}
              </button>
            </div>

            <div className="mt-4 p-3 bg-secondary-offWhite rounded-lg">
              <p className="text-sm text-secondary-gray mb-2">
                When enabled, the following values will be blurred on financial reports:
              </p>
              <ul className="text-xs text-secondary-gray space-y-1 ml-4 list-disc">
                <li>
                  Balance Sheet: Total Assets, Total Equity, Total Liabilities, Working Capital,
                  Inventory
                </li>
                <li>Income Statement: Revenue, Gross Profit, Net Income, Expenses</li>
                <li>Cash Flow: Net Cash Flow, Operating/Investing/Financing activities</li>
                <li>Trial Balance: Account balances and totals</li>
              </ul>
            </div>
          </div>

          {/* Demo Login Link */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-primary-black mb-3">Demo Login Link</h2>
            <div className="flex items-center gap-3">
              <input
                type="text"
                readOnly
                value={`${window.location.origin}/demo-login`}
                className="flex-1 px-4 py-2 bg-secondary-offWhite border border-secondary-lightGray rounded-lg text-sm"
              />
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/demo-login`);
                  toast.success('Link copied to clipboard');
                }}
                className="px-4 py-2 bg-primary-charcoal text-white rounded-lg hover:bg-primary-black transition-colors text-sm"
              >
                Copy
              </button>
            </div>
            <p className="text-xs text-secondary-gray mt-2">
              Share this link with people who need demo access.
            </p>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-6 py-3 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-gold/90 transition-colors disabled:opacity-50"
            >
              {saving ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Save Settings
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DemoSettingsPage;
